import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Lock, CalendarDays, CreditCard, Mail } from "lucide-react";
import { siteData } from "../mock";

export default function PrivacyPolicy() {
  const updated = "September 2025";
  const brand = siteData.brand.name;
  const letters = brand.split("");

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Top bar */}
      <header className="border-b border-white/8 bg-[#0a0a0a]/95 sticky top-0 z-40 backdrop-blur">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex flex-col leading-none group">
            <span className="font-wordmark font-bold text-2xl md:text-3xl tracking-wide">
              {letters.map((l, i) => (
                <span key={i} className={i === 3 ? "deal-yellow" : "text-white"}>
                  {l}
                </span>
              ))}
            </span>
            <span className="font-wordmark text-white/90 text-sm tracking-wide mt-0.5">
              {siteData.brand.tagline}
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/70 hover:text-[#e10600] text-sm tracking-[0.2em] font-heading font-semibold transition-colors"
          >
            <ArrowLeft size={16} />
            BACK TO HOME
          </Link>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-6 lg:px-10 py-14">
        {/* Title */}
        <p className="deal-yellow font-heading tracking-[0.3em] text-xs mb-3">
          PRIVACY POLICY
        </p>
        <h1 className="font-display font-bold text-white text-5xl md:text-6xl tracking-wide leading-none mb-4">
          YOUR PRIVACY MATTERS
        </h1>
        <p className="text-white/60 text-sm">Last updated: {updated}</p>

        {/* Trust row */}
        <div className="flex flex-wrap items-center gap-3 mt-8 mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5">
            <ShieldCheck size={14} className="text-emerald-400" strokeWidth={2.4} />
            <span className="text-white/85 text-[10px] tracking-[0.2em] font-heading font-semibold">
              SSL SECURED
            </span>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5">
            <Lock size={14} className="text-emerald-400" strokeWidth={2.4} />
            <span className="text-white/85 text-[10px] tracking-[0.2em] font-heading font-semibold">
              NO SPAM · NO RESELLING
            </span>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5">
            <CreditCard size={14} className="text-emerald-400" strokeWidth={2.4} />
            <span className="text-white/85 text-[10px] tracking-[0.2em] font-heading font-semibold">
              SECURE PAYMENTS · SQUARE
            </span>
          </div>
        </div>

        <div className="space-y-10 text-white/85 leading-relaxed">
          <section>
            <h2 className="font-display font-bold text-white text-2xl mb-3 tracking-wide">
              1. Who we are
            </h2>
            <p>
              THEDEAL FITNESS is a certified personal training business owned and operated by
              Chris Garrett at 10521 Croatia Way, Manassas, VA 20109. When we say "we," "us," or
              "our," we're referring to THEDEAL FITNESS.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-2xl mb-3 tracking-wide">
              2. Information we collect
            </h2>
            <p className="mb-3">We only collect what we need to serve you as a client:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <span className="font-semibold text-white">Contact details</span> — name, email
                address, and (optionally) phone number, submitted through the contact form or
                during the booking flow
              </li>
              <li>
                <span className="font-semibold text-white">Booking details</span> — the package
                you selected, session dates you chose on Calendly, and any notes you shared
              </li>
              <li>
                <span className="font-semibold text-white">Payment confirmations</span> — a
                notification that a payment was made. Card numbers and full payment data are
                handled entirely by Square and never touch our servers
              </li>
              <li>
                <span className="font-semibold text-white">Basic analytics</span> — anonymous
                usage data (pages viewed, buttons clicked) to help us improve the site
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-2xl mb-3 tracking-wide">
              3. How we use your information
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Respond to inquiries you submit through the contact form</li>
              <li>Schedule, confirm, and remind you about training sessions</li>
              <li>Send payment receipts and booking confirmations</li>
              <li>Improve our services based on aggregated usage data</li>
            </ul>
            <p className="mt-3">
              We <span className="font-semibold text-white">never sell, rent, or share</span> your
              personal information with advertisers or unrelated third parties.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-2xl mb-3 tracking-wide">
              4. Third-party services we use
            </h2>
            <p className="mb-3">
              A few trusted services help us run the booking and payment flow. Each has their own
              privacy policy you can review:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <CalendarDays size={14} className="text-[#e10600]" />
                  <p className="font-heading font-bold tracking-[0.15em] text-xs">CALENDLY</p>
                </div>
                <p className="text-white/70 text-sm">Handles session scheduling.</p>
                <a
                  href="https://calendly.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#e10600] hover:underline text-xs"
                >
                  calendly.com/privacy
                </a>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard size={14} className="text-[#e10600]" />
                  <p className="font-heading font-bold tracking-[0.15em] text-xs">SQUARE</p>
                </div>
                <p className="text-white/70 text-sm">Processes card payments securely.</p>
                <a
                  href="https://squareup.com/us/en/legal/general/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#e10600] hover:underline text-xs break-all"
                >
                  squareup.com privacy
                </a>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Mail size={14} className="text-[#e10600]" />
                  <p className="font-heading font-bold tracking-[0.15em] text-xs">FORMSUBMIT</p>
                </div>
                <p className="text-white/70 text-sm">Delivers form emails to Chris.</p>
                <a
                  href="https://formsubmit.co/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#e10600] hover:underline text-xs"
                >
                  formsubmit.co/privacy
                </a>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-2xl mb-3 tracking-wide">
              5. How we protect your data
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>The entire site is served over HTTPS with SSL encryption</li>
              <li>Card payments are handled by Square — PCI DSS compliant</li>
              <li>We limit internal access to your contact info to Chris only</li>
              <li>
                Contact-form submissions are stored temporarily in secure email — deleted after
                the client relationship ends unless retention is required by law
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-2xl mb-3 tracking-wide">
              6. Your rights
            </h2>
            <p className="mb-3">You can, at any time, ask us to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Show you what personal data we have about you</li>
              <li>Correct or update your details</li>
              <li>Delete your data from our records</li>
              <li>Stop contacting you</li>
            </ul>
            <p className="mt-3">
              Just email{" "}
              <a
                href={`mailto:${siteData.contact.email}`}
                className="text-[#e10600] hover:underline font-semibold"
              >
                {siteData.contact.email}
              </a>{" "}
              and we'll act within 30 days.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-2xl mb-3 tracking-wide">
              7. Cookies
            </h2>
            <p>
              This site uses essential cookies to load the booking widget and remember your
              details across the checkout flow (so you don't have to re-type your name/email each
              step). We do not use advertising or tracking cookies.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-2xl mb-3 tracking-wide">
              8. Children's privacy
            </h2>
            <p>
              Our services are intended for individuals 18 and older, or minors participating with
              parental consent. We do not knowingly collect data from children under 13. If you
              believe we have, please contact us and we will delete it immediately.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-2xl mb-3 tracking-wide">
              9. Updates to this policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. When we do, we'll change the
              "Last updated" date at the top of the page. Material changes will be highlighted on
              the site.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-2xl mb-3 tracking-wide">
              10. Contact
            </h2>
            <p>
              Questions? Reach out to Chris at{" "}
              <a
                href={`mailto:${siteData.contact.email}`}
                className="text-[#e10600] hover:underline font-semibold"
              >
                {siteData.contact.email}
              </a>{" "}
              or{" "}
              <a
                href={`tel:${siteData.contact.phone}`}
                className="text-[#e10600] hover:underline font-semibold"
              >
                {siteData.contact.phone}
              </a>
              .
            </p>
          </section>
        </div>

        <div className="mt-14 pt-8 border-t border-white/8 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/70 hover:text-[#e10600] text-sm tracking-[0.2em] font-heading font-semibold transition-colors"
          >
            <ArrowLeft size={16} />
            BACK TO HOME
          </Link>
          <p className="text-white/40 text-xs tracking-[0.15em]">
            © {new Date().getFullYear()} THEDEAL FITNESS
          </p>
        </div>
      </main>
    </div>
  );
}
